import type { RfqPackage, RfqProject, RfqSubmission, SupplierOrg, VendorWorkType } from '../types';
import { formatCncQuoteSummary, packageBoardFeet } from './cncVendorExport';
import { formatQuoteTotal, submissionQuoteTotal } from './rfqQuoteTotal';
import {
  approvedSubmissionIdsForReleaseSelection,
  approvedSupplierOrgIdsForReleaseSelection,
  isPrePortalPackage,
  PRE_PORTAL_RELEASE_VERSION_INDEX,
  releaseKeyForPackage,
  releaseVersionLabel,
} from './timberRfqPrePortalVersion';
import { latestQuoteSubmissions } from './rfqSubmissionHistory';
import { timberQuoteStreamKey } from './timberQuoteStreamKey';
import { resolveOtherQuoteSupplierName } from './otherQuoteVendor';

export type QuoteRejectionTarget = {
  orgId: string;
  orgName: string;
  quoteLabel: string;
  submissionId?: string;
  /**
   * Older active submissions superseded by `submissionId` (CNC re-quotes).
   * Rejection must stamp these too or they keep surfacing as open quotes.
   */
  extraSubmissionIds?: string[];
  versionLabel?: string;
};

export function quoteRejectionTargetKey(target: QuoteRejectionTarget): string {
  return target.submissionId || `${target.orgId}::${target.versionLabel ?? ''}::${target.quoteLabel}`;
}

export function isRfqQuoteRejected(sub: Pick<RfqSubmission, 'rejectedAt'>): boolean {
  return sub.rejectedAt instanceof Date && !Number.isNaN(sub.rejectedAt.getTime());
}

export function isRfqQuoteRejectionNoticeSent(
  sub: Pick<RfqSubmission, 'rejectionNoticeSentAt'>,
): boolean {
  return (
    sub.rejectionNoticeSentAt instanceof Date && !Number.isNaN(sub.rejectionNoticeSentAt.getTime())
  );
}

function isRejectionCandidateSubmission(sub: RfqSubmission): boolean {
  if (sub.notBidding) return false;
  const hasQuote =
    !!sub.storagePath
    || !!sub.cncQuote
    || (typeof sub.quoteTotal === 'number' && sub.quoteTotal > 0);
  if (!hasQuote) return false;
  if (isRfqQuoteRejected(sub) && isRfqQuoteRejectionNoticeSent(sub)) return false;
  return true;
}

function isPrePortalDeclinePackage(
  pkg: RfqPackage,
  activeSubs: RfqSubmission[],
): boolean {
  return isPrePortalPackage(pkg) || activeSubs.some(sub => sub.legacyImport === true);
}

export function timberQuoteDeclineTarget(
  sub: RfqSubmission,
  orgId: string,
  orgName: string,
  pkg?: RfqPackage | null,
): QuoteRejectionTarget {
  return {
    orgId,
    orgName,
    quoteLabel: timberSubmissionQuoteLabel(sub),
    submissionId: sub.id,
    versionLabel: pkg ? packageVersionLabel(pkg) : undefined,
  };
}

function timberSubmissionQuoteLabel(sub: RfqSubmission): string {
  const species = sub.quotedSpecies?.trim() || sub.fileName?.replace(/\.pdf$/i, '').trim() || 'Quote';
  const total = formatQuoteTotal(submissionQuoteTotal(sub));
  return total !== '—' ? `${species} — ${total}` : species;
}

export function cncQuoteDeclineTarget(
  sub: RfqSubmission,
  orgId: string,
  orgName: string,
  pkg?: RfqPackage | null,
  boardFeet = 0,
): QuoteRejectionTarget {
  return {
    orgId,
    orgName,
    quoteLabel: cncSubmissionQuoteLabel(sub, pkg ?? undefined, boardFeet),
    submissionId: sub.id,
    versionLabel: pkg ? packageVersionLabel(pkg) : undefined,
  };
}

function cncSubmissionQuoteLabel(sub: RfqSubmission, pkg: RfqPackage | undefined, boardFeet: number): string {
  const total = formatQuoteTotal(submissionQuoteTotal(sub));
  const summary = sub.cncQuote ? formatCncQuoteSummary(sub.cncQuote, pkg ? packageBoardFeet(pkg) : boardFeet) : '';
  if (total !== '—') {
    return summary ? `${summary} — ${total}` : total;
  }
  return summary || 'CNC quote';
}

function otherSubmissionQuoteLabel(sub: RfqSubmission): string {
  const label = sub.quoteLabel?.trim() || sub.fileName?.replace(/\.pdf$/i, '').trim() || 'Quote';
  const total = formatQuoteTotal(submissionQuoteTotal(sub));
  return total !== '—' ? `${label} — ${total}` : label;
}

function orgNameFor(orgId: string, supplierOrgById: Map<string, SupplierOrg>): string {
  return supplierOrgById.get(orgId)?.name?.trim() || orgId;
}

function packageVersionLabel(pkg: RfqPackage): string {
  const index =
    typeof pkg.releaseVersionIndex === 'number'
      ? pkg.releaseVersionIndex
      : isPrePortalPackage(pkg)
        ? PRE_PORTAL_RELEASE_VERSION_INDEX
        : 1;
  return releaseVersionLabel(index);
}

function allAwardedSubmissionIds(project: RfqProject): Set<string> {
  const ids = new Set<string>();
  for (const id of project.approvedSubmissionIds ?? []) {
    if (id) ids.add(id);
  }
  for (const list of Object.values(project.approvedSubmissionIdsByRelease ?? {})) {
    for (const id of list ?? []) {
      if (id) ids.add(id);
    }
  }
  return ids;
}

function cncProjectHasAward(project: RfqProject): boolean {
  if (project.approvedSupplierOrgId) return true;
  if (Object.values(project.approvedSupplierOrgIdByRelease ?? {}).some(Boolean)) return true;
  return Object.values(project.approvedSupplierOrgIdsByRelease ?? {}).some(
    ids => (ids ?? []).length > 0,
  );
}

export function releaseHasAwardedSupplier(
  project: RfqProject,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  selectedReleaseId: string | null,
  workType: VendorWorkType,
): boolean {
  if (workType === 'cnc_quote') {
    return approvedSupplierOrgIdsForReleaseSelection(project, selectedReleaseId).length > 0;
  }
  return (
    approvedSubmissionIdsForReleaseSelection(
      project,
      packages,
      submissionsByPackageId,
      selectedReleaseId,
    ).length > 0
  );
}

export function projectHasAwardedSupplier(
  project: RfqProject,
  packages: RfqPackage[],
  submissionsByPackageId: Map<string, RfqSubmission[]>,
  workType: VendorWorkType,
): boolean {
  if (workType === 'cnc_quote') return cncProjectHasAward(project);
  if (allAwardedSubmissionIds(project).size > 0) return true;
  const releaseIds = Array.from(new Set(packages.map(pkg => releaseKeyForPackage(pkg))));
  for (const releaseId of releaseIds) {
    if (releaseHasAwardedSupplier(project, packages, submissionsByPackageId, releaseId, workType)) {
      return true;
    }
  }
  return false;
}

function latestActiveSub(subs: RfqSubmission[]): RfqSubmission | null {
  if (!subs.length) return null;
  return subs.reduce((a, b) => {
    const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
    const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
    return tb > ta ? b : a;
  });
}

function pushTarget(
  targets: QuoteRejectionTarget[],
  seen: Set<string>,
  target: QuoteRejectionTarget,
) {
  const key = quoteRejectionTargetKey(target);
  if (seen.has(key)) return;
  seen.add(key);
  targets.push(target);
}

export function groupQuoteRejectionTargetsForEmail(
  targets: QuoteRejectionTarget[],
): Array<{ supplierOrgId: string; quoteLabel: string; submissionId?: string }> {
  const byOrg = new Map<string, QuoteRejectionTarget[]>();
  for (const target of targets) {
    const list = byOrg.get(target.orgId) ?? [];
    list.push(target);
    byOrg.set(target.orgId, list);
  }
  return Array.from(byOrg.entries()).map(([orgId, rows]) => ({
    supplierOrgId: orgId,
    quoteLabel: rows
      .map(row => (row.versionLabel ? `${row.versionLabel} ${row.quoteLabel}` : row.quoteLabel))
      .join('; '),
    submissionId: rows.find(row => row.submissionId)?.submissionId,
  }));
}

export function buildQuoteRejectionTargets(params: {
  workType: VendorWorkType;
  project: RfqProject;
  projectSupplierOrgIds: string[];
  packages: RfqPackage[];
  submissionsByPackageId: Map<string, RfqSubmission[]>;
  selectedReleaseId: string | null;
  supplierOrgById: Map<string, SupplierOrg>;
}): QuoteRejectionTarget[] {
  const { workType, project, packages, submissionsByPackageId, supplierOrgById } = params;

  const hasAward = projectHasAwardedSupplier(project, packages, submissionsByPackageId, workType);
  const awardedSubmissionIds = allAwardedSubmissionIds(project);
  const targets: QuoteRejectionTarget[] = [];
  const seen = new Set<string>();

  for (const pkg of packages) {
    if (!pkg.id || !pkg.supplierOrgId) continue;
    const orgId = pkg.supplierOrgId;
    const versionLabel = packageVersionLabel(pkg);
    const activeSubs = (submissionsByPackageId.get(pkg.id) ?? []).filter(
      isRejectionCandidateSubmission,
    );
    if (!activeSubs.length) continue;
    if (!hasAward) {
      const canDeclinePrePortal =
        (workType === 'timber_rfq' || workType === 'cnc_quote')
        && isPrePortalDeclinePackage(pkg, activeSubs);
      if (!canDeclinePrePortal) continue;
    }

    if (workType === 'cnc_quote') {
      const awardedOnThisRelease = approvedSupplierOrgIdsForReleaseSelection(
        project,
        releaseKeyForPackage(pkg),
      );
      if (awardedOnThisRelease.includes(orgId)) continue;
      const latestSub = latestActiveSub(activeSubs);
      if (!latestSub) continue;
      pushTarget(targets, seen, {
        orgId,
        orgName: orgNameFor(orgId, supplierOrgById),
        quoteLabel: cncSubmissionQuoteLabel(latestSub, pkg, project.boardFeet ?? 0),
        submissionId: latestSub.id,
        extraSubmissionIds: activeSubs
          .map(sub => sub.id)
          .filter((id): id is string => !!id && id !== latestSub.id),
        versionLabel,
      });
      continue;
    }

    const quoteSubs =
      workType === 'other_quote'
        ? activeSubs
        : latestQuoteSubmissions(activeSubs, s => timberQuoteStreamKey(s));

    for (const sub of quoteSubs) {
      if (sub.id && awardedSubmissionIds.has(sub.id)) continue;
      pushTarget(targets, seen, {
        orgId,
        orgName:
          workType === 'other_quote'
            ? resolveOtherQuoteSupplierName(sub, orgId, supplierOrgById)
            : orgNameFor(orgId, supplierOrgById),
        quoteLabel:
          workType === 'other_quote' ? otherSubmissionQuoteLabel(sub) : timberSubmissionQuoteLabel(sub),
        submissionId: sub.id,
        versionLabel,
      });
    }
  }

  return targets.sort((a, b) => {
    const org = a.orgName.localeCompare(b.orgName);
    if (org !== 0) return org;
    const version = (a.versionLabel ?? '').localeCompare(b.versionLabel ?? '');
    if (version !== 0) return version;
    return a.quoteLabel.localeCompare(b.quoteLabel);
  });
}
